import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import { useAdmin } from '../../contexts/AdminContext';
import { Category, formatCategoryLabel } from '../../types';

const Header: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchCategory, setSearchCategory] = useState('all');
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const { isLoggedIn, user, logout } = useAuth();
  const { itemCount } = useCart();
  const { flatCategories } = useAdmin();
  const navigate = useNavigate();
  const location = useLocation();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const dropdownTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const searchKeywords = [
    'Tadalafil', 'Cialis', 'Isotretinoin',
    'Finpecia', 'Xenical', 'Sildenafil', 'Cenforce',
  ];

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Cmd+K shortcut for search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsSearchOpen(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleSearch = (query: string) => {
    if (query.trim()) {
      const params = new URLSearchParams({ search: query.trim() });
      if (searchCategory !== 'all') params.set('category', searchCategory);
      navigate(`/shop?${params.toString()}`);
      setIsSearchOpen(false);
      setSearchQuery('');
      setIsMobileMenuOpen(false);
    }
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleSearch(searchQuery);
    if (e.key === 'Escape') setIsSearchOpen(false);
  };

  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchOpen]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [location.pathname]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  const topLevelCategories = useMemo(() => {
    const desiredOrder = ['sexual-health', 'hair-loss', 'skin-care', 'weight-management'];
    const hiddenCategories = ['beauty-skincare'];
    const topLevelCats = flatCategories.filter(cat =>
      cat.level === 0 && !hiddenCategories.includes(cat.name)
    );
    const sortedCategories = desiredOrder
      .map(name => topLevelCats.find(cat => cat.name === name))
      .filter((cat): cat is Category => cat !== undefined);
    const remainingCategories = topLevelCats
      .filter(cat => !desiredOrder.includes(cat.name));
    return [...sortedCategories, ...remainingCategories];
  }, [flatCategories]);

  const hierarchicalCategories = useMemo(() => {
    return topLevelCategories.map(topCat => ({
      ...topCat,
      children: flatCategories.filter(cat => cat.parentId === topCat.id)
    }));
  }, [topLevelCategories, flatCategories]);

  const desktopNavLinks = [
    { to: '/shop/sexual-health', label: 'Sexual Health', dropdownKey: 'sexual-health', hasDropdown: true },
    { to: '/shop/hair-loss', label: 'Hair Loss', dropdownKey: 'hair-loss', hasDropdown: true },
    { to: '/shop/weight-management', label: 'Weight Management', dropdownKey: 'weight-management', hasDropdown: true },
    { to: '/shop', label: 'More', dropdownKey: 'more', hasDropdown: true },
    { to: '/guides', label: 'Guides', hasDropdown: false },
    // Reviews + Offers moved into the "More" dropdown footer to keep top nav lean.
  ];

  const mobileNavLinks = [
    { to: '/shop', label: 'Shop' },
    { to: '/guides', label: 'Guides' },
    { to: '/review', label: 'Reviews' },
    { to: '/offers', label: 'Offers' },
    { to: '/mission', label: 'About' },
  ];

  const isActive = (path: string) => {
    if (path === '/shop') return location.pathname === '/shop';
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Find the category data for the currently hovered dropdown
  const activeDropdownCategory = activeDropdown
    ? hierarchicalCategories.find(c => c.name === activeDropdown)
    : null;

  const handleDropdownEnter = (label: string) => {
    if (dropdownTimeoutRef.current) clearTimeout(dropdownTimeoutRef.current);
    setActiveDropdown(label);
  };

  const handleDropdownLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => setActiveDropdown(null), 150);
  };

  const categoryIcons: Record<string, React.ReactNode> = {
    'sexual-health': (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
      </svg>
    ),
    'hair-loss': (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
      </svg>
    ),
    'skin-care': (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M15.182 15.182a4.5 4.5 0 01-6.364 0M21 12a9 9 0 11-18 0 9 9 0 0118 0zM9.75 9.75c0 .414-.168.75-.375.75S9 10.164 9 9.75 9.168 9 9.375 9s.375.336.375.75zm-.375 0h.008v.015h-.008V9.75zm5.625 0c0 .414-.168.75-.375.75s-.375-.336-.375-.75.168-.75.375-.75.375.336.375.75zm-.375 0h.008v.015h-.008V9.75z" />
      </svg>
    ),
    'weight-management': (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
      </svg>
    ),
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50">
        {/* Announcement bar — cream with gold accent */}
        <div className={`bg-cream-200 overflow-hidden transition-all duration-500 ease-spring ${isScrolled ? 'h-0 opacity-0' : 'h-9 opacity-100'}`}>
          <div className="section-container flex items-center justify-center h-full gap-2.5">
            <div className="w-1.5 h-1.5 rounded-full bg-gold-400" />
            <p className="text-micro font-medium tracking-wide text-forest-800">
              Free worldwide shipping on orders over $150
            </p>
          </div>
        </div>

        {/* Main header bar */}
        <div className={`transition-all duration-600 ease-spring ${
          isScrolled
            ? 'mx-4 sm:mx-6 lg:mx-auto lg:max-w-5xl mt-3 rounded-2xl bg-cream-50/90 backdrop-blur-2xl shadow-elevated border border-warm-200/60'
            : 'bg-cream-50/0'
        }`}>
          <div className={`${isScrolled ? 'px-4 sm:px-5' : 'section-container'}`}>
            <div className={`flex items-center justify-between transition-all duration-500 ${isScrolled ? 'h-[52px]' : 'h-[64px] lg:h-[72px]'}`}>

              {/* Left: Logo */}
              <Link to="/" className="flex items-baseline gap-0 group flex-shrink-0">
                <span className={`font-display font-bold tracking-[-0.04em] text-forest-800 transition-all duration-500 group-hover:text-forest-600 ${isScrolled ? 'text-[20px]' : 'text-[24px] sm:text-[28px]'}`}>
                  Libera
                </span>
                <span className={`font-display font-bold tracking-[-0.04em] text-gold-400 transition-all duration-500 ${isScrolled ? 'text-[20px]' : 'text-[24px] sm:text-[28px]'}`}>
                  Cure
                </span>
              </Link>

              {/* Center: Navigation (desktop) */}
              <nav className="hidden lg:flex items-center gap-0.5">
                {desktopNavLinks.map((link) => (
                  <div
                    key={link.to}
                    className="relative group"
                    onMouseEnter={() => link.hasDropdown && handleDropdownEnter(link.dropdownKey!)}
                    onMouseLeave={() => link.hasDropdown && handleDropdownLeave()}
                  >
                    <Link
                      to={link.to}
                      className={`relative px-4 py-2 text-[13px] font-medium transition-all duration-300 rounded-lg ${
                        isActive(link.to)
                          ? 'text-forest-900 bg-forest-50'
                          : 'text-warm-500 hover:text-forest-900 hover:bg-forest-50/60'
                      }`}
                    >
                      {link.label}
                      {link.hasDropdown && (
                        <svg className={`inline-block w-3 h-3 ml-1 transition-transform duration-300 ${activeDropdown === link.dropdownKey ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                        </svg>
                      )}
                    </Link>
                    {link.hasDropdown && link.dropdownKey !== 'more' && activeDropdown === link.dropdownKey && activeDropdownCategory && (
                      <div
                        className="absolute top-full left-0 pt-2 z-[45]"
                        onMouseEnter={() => handleDropdownEnter(activeDropdown!)}
                        onMouseLeave={handleDropdownLeave}
                      >
                        <div
                          className="bg-cream-50/95 backdrop-blur-2xl rounded-2xl shadow-float border border-warm-200 p-5 animate-fade-up w-[220px]"
                          style={{ animationDuration: '0.15s' }}
                        >
                          <Link
                            to={`/shop/${encodeURIComponent(activeDropdownCategory.name)}`}
                            className="flex items-center gap-2.5 mb-3 pb-2.5 border-b border-warm-200 group"
                          >
                            <div className="w-7 h-7 rounded-lg bg-forest-50 flex items-center justify-center text-forest-600 flex-shrink-0">
                              {categoryIcons[activeDropdownCategory.name] || (
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
                                </svg>
                              )}
                            </div>
                            <span className="text-[13px] font-semibold text-forest-900 group-hover:text-forest-700 transition-colors">
                              {formatCategoryLabel(activeDropdownCategory.name)}
                            </span>
                          </Link>
                          <ul className="space-y-0.5">
                            {activeDropdownCategory.children.map((sub) => (
                              <li key={sub.id}>
                                <Link
                                  to={`/shop/${encodeURIComponent(activeDropdownCategory.name)}/${encodeURIComponent(sub.name)}`}
                                  className="block px-2 py-1.5 rounded-lg text-[13px] text-warm-500 hover:text-forest-800 hover:bg-forest-50/60 transition-colors"
                                >
                                  {formatCategoryLabel(sub.name)}
                                </Link>
                              </li>
                            ))}
                          </ul>
                          <Link
                            to={`/shop/${encodeURIComponent(activeDropdownCategory.name)}`}
                            className="block mt-3 pt-3 border-t border-warm-200 text-[12px] font-medium text-forest-600 hover:text-forest-800 transition-colors"
                          >
                            View all →
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </nav>

              {/* Right: Actions */}
              <div className="flex items-center gap-0.5">
                {/* Search trigger */}
                <button
                  onClick={() => setIsSearchOpen(true)}
                  className={`hidden sm:flex items-center gap-2 text-warm-400 hover:text-forest-700 rounded-lg hover:bg-forest-50/60 transition-all duration-200 cursor-pointer ${
                    isScrolled ? 'px-3 py-1.5' : 'px-3.5 py-2'
                  }`}
                  aria-label="Search"
                >
                  <svg className="w-[16px] h-[16px]" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                  </svg>
                  <span className="text-[12px] font-medium">Search</span>
                  <kbd className="hidden md:inline-flex items-center gap-0.5 text-[10px] text-warm-400 bg-warm-100 px-1.5 py-0.5 rounded font-mono">
                    ⌘K
                  </kbd>
                </button>

                {/* Mobile search */}
                <button
                  onClick={() => setIsSearchOpen(true)}
                  className="sm:hidden p-2.5 text-warm-500 hover:text-forest-900 rounded-lg hover:bg-forest-50 transition-all duration-200 cursor-pointer"
                  aria-label="Search"
                >
                  <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                  </svg>
                </button>

                {/* Account */}
                {!isLoggedIn ? (
                  <Link
                    to="/login"
                    className="hidden sm:flex items-center gap-1.5 px-3 py-2 text-[13px] text-warm-500 font-medium rounded-lg hover:text-forest-900 hover:bg-forest-50/60 transition-all duration-200"
                  >
                    <svg className="w-[17px] h-[17px]" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                    </svg>
                  </Link>
                ) : (
                  <Link
                    to="/mypage"
                    className="hidden sm:flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-forest-50/60 transition-all duration-200"
                  >
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-forest-600 to-forest-800 flex items-center justify-center ring-2 ring-cream-50">
                      <span className="text-[11px] font-bold text-cream-100">
                        {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                      </span>
                    </div>
                  </Link>
                )}

                {/* Cart */}
                <Link
                  to="/cart"
                  className={`relative text-forest-800 hover:text-forest-600 rounded-lg hover:bg-forest-50/60 transition-all duration-200 ${isScrolled ? 'p-2' : 'p-2.5'}`}
                >
                  <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5V6a3.75 3.75 0 10-7.5 0v4.5m11.356-1.993l1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 01-1.12-1.243l1.264-12A1.125 1.125 0 015.513 7.5h12.974c.576 0 1.059.435 1.119 1.007zM8.625 10.5a.375.375 0 11-.75 0 .375.375 0 01.75 0zm7.5 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                  </svg>
                  {itemCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 bg-gold-400 text-forest-900 text-[10px] font-bold flex items-center justify-center rounded-full min-w-[18px] h-[18px] ring-2 ring-cream-50">
                      {itemCount > 99 ? '99+' : itemCount}
                    </span>
                  )}
                </Link>

                {/* Mobile menu button */}
                <button
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="lg:hidden p-2.5 text-forest-800 hover:text-forest-600 rounded-lg hover:bg-forest-50 transition-all duration-200 cursor-pointer ml-0.5"
                  aria-label="Menu"
                >
                  <div className="w-[18px] h-[14px] relative flex flex-col justify-between">
                    <span className={`block h-[1.5px] bg-current rounded-full transition-all duration-300 origin-center ${isMobileMenuOpen ? 'rotate-45 translate-y-[6.25px]' : ''}`} />
                    <span className={`block h-[1.5px] bg-current rounded-full transition-all duration-300 ${isMobileMenuOpen ? 'opacity-0 scale-x-0' : ''}`} />
                    <span className={`block h-[1.5px] bg-current rounded-full transition-all duration-300 origin-center ${isMobileMenuOpen ? '-rotate-45 -translate-y-[6.25px]' : ''}`} />
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom line */}
        <div className={`h-px bg-warm-200 transition-opacity duration-500 ${isScrolled ? 'opacity-0' : 'opacity-30'}`} />
      </header>

      {/* Category dropdowns are now rendered inline inside each nav item — see below */}

      {/* More Dropdown — remaining categories + utility links (rendered inline in nav) */}
      {activeDropdown === 'more' && (
        <div
          className="fixed z-[45] left-0 right-0 pointer-events-none"
          style={{ top: isScrolled ? '68px' : '104px' }}
        >
          <div className="section-container flex justify-end">
            <div
              className="bg-cream-50/95 backdrop-blur-2xl rounded-2xl shadow-float border border-warm-200 p-6 animate-fade-up pointer-events-auto max-w-4xl max-h-[75vh] overflow-y-auto"
              style={{ animationDuration: '0.15s' }}
              onMouseEnter={() => handleDropdownEnter('more')}
              onMouseLeave={handleDropdownLeave}
            >
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4">
                {hierarchicalCategories
                  .filter(c => !['sexual-health', 'hair-loss'].includes(c.name))
                  .map((cat) => (
                    <div key={cat.id}>
                      <Link
                        to={`/shop/${encodeURIComponent(cat.name)}`}
                        className="flex items-center gap-2.5 mb-3 pb-2 border-b border-warm-200 group hover:border-forest-300 transition-colors"
                      >
                        <div className="w-7 h-7 rounded-lg bg-warm-100 group-hover:bg-forest-100 flex items-center justify-center text-warm-400 group-hover:text-forest-600 transition-colors flex-shrink-0">
                          {categoryIcons[cat.name] || (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
                            </svg>
                          )}
                        </div>
                        <span className="text-[13px] font-semibold text-forest-900 group-hover:text-forest-700 transition-colors">
                          {formatCategoryLabel(cat.name)}
                        </span>
                      </Link>
                      {cat.children && cat.children.length > 0 && (
                        <ul className="space-y-0.5">
                          {cat.children.slice(0, 6).map((sub) => (
                            <li key={sub.id}>
                              <Link
                                to={`/shop/${encodeURIComponent(cat.name)}/${encodeURIComponent(sub.name)}`}
                                className="block px-2 py-1.5 rounded-lg text-[12px] text-warm-500 hover:text-forest-800 hover:bg-forest-50/60 transition-colors"
                              >
                                {formatCategoryLabel(sub.name)}
                              </Link>
                            </li>
                          ))}
                          {cat.children.length > 6 && (
                            <li>
                              <Link
                                to={`/shop/${encodeURIComponent(cat.name)}`}
                                className="block px-2 py-1 text-[11px] text-gold-500 hover:text-gold-600 font-medium"
                              >
                                +{cat.children.length - 6} more →
                              </Link>
                            </li>
                          )}
                        </ul>
                      )}
                    </div>
                  ))}
              </div>
              <div className="mt-5 pt-4 border-t border-warm-200 flex items-center justify-between gap-4">
                <Link to="/shop" className="text-[13px] font-medium text-forest-600 hover:text-forest-800 transition-colors flex items-center gap-1.5">
                  Browse all products
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                  </svg>
                </Link>
                <div className="flex items-center gap-5">
                  <Link to="/review" className="text-[12px] font-medium text-warm-500 hover:text-forest-700 transition-colors">
                    Reviews
                  </Link>
                  <Link to="/offers" className="text-[12px] font-medium text-warm-500 hover:text-forest-700 transition-colors">
                    Offers
                  </Link>
                  <Link to="/mission" className="text-[12px] font-medium text-warm-400 hover:text-forest-600 transition-colors">
                    About us →
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Spacer */}
      <div className={`transition-all duration-500 ${isScrolled ? 'h-[88px]' : 'h-[100px] lg:h-[108px]'}`} />

      {/* Search Overlay — Command palette style */}
      {isSearchOpen && (
        <div className="fixed inset-0 z-[60]">
          <div
            className="absolute inset-0 bg-forest-950/50 backdrop-blur-md animate-fade-in"
            onClick={() => setIsSearchOpen(false)}
          />
          <div className="relative max-w-xl mx-auto mt-[18vh] px-5 animate-fade-up" style={{ animationDuration: '0.2s' }}>
            <div className="bg-cream-50 rounded-2xl shadow-float overflow-hidden border border-warm-200/50">
              {/* Search input */}
              <div className="flex items-center gap-3 px-5 py-4">
                <svg className="w-5 h-5 text-gold-400 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                </svg>
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleSearchKeyPress}
                  placeholder="Search medications..."
                  className="flex-1 text-[16px] text-forest-900 placeholder-warm-400 outline-none bg-transparent tracking-tight"
                  autoFocus
                />
                <kbd className="text-[11px] text-warm-400 bg-warm-100 px-2 py-1 rounded font-mono">ESC</kbd>
              </div>

              <div className="border-t border-warm-200 px-5 pb-5">
                {/* Category filter */}
                <div className="flex items-center gap-2 mt-4 mb-4">
                  <span className="text-[11px] font-semibold text-warm-400 uppercase tracking-widest">In</span>
                  <select
                    value={searchCategory}
                    onChange={(e) => setSearchCategory(e.target.value)}
                    className="text-[13px] text-forest-800 font-medium bg-warm-100 border-0 rounded-lg px-3 py-1.5 outline-none cursor-pointer hover:bg-warm-150 transition-colors"
                  >
                    <option value="all">All Categories</option>
                    {topLevelCategories.map(cat => (
                      <option key={cat.id} value={cat.name}>{formatCategoryLabel(cat.name)}</option>
                    ))}
                  </select>
                </div>

                {/* Quick links */}
                <p className="text-[11px] font-semibold text-warm-400 uppercase tracking-[0.12em] mb-2.5">Popular</p>
                <div className="flex flex-wrap gap-1.5">
                  {searchKeywords.map((keyword) => (
                    <button
                      key={keyword}
                      onClick={() => handleSearch(keyword)}
                      className="px-3 py-1.5 bg-warm-100 rounded-lg text-[13px] text-warm-600 font-medium hover:bg-forest-50 hover:text-forest-800 transition-all duration-200 cursor-pointer"
                    >
                      {keyword}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Drawer */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-[55] lg:hidden">
          <div
            className="absolute inset-0 bg-forest-950/50 backdrop-blur-md animate-fade-in"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          <div className="absolute right-0 top-0 bottom-0 w-full sm:w-[400px] bg-cream-50 overflow-y-auto animate-slide-in-right">
            {/* Drawer Header */}
            <div className="flex items-center justify-between px-6 h-[64px] border-b border-warm-200">
              <Link to="/" onClick={() => setIsMobileMenuOpen(false)} className="flex items-baseline gap-0">
                <span className="font-display text-[20px] font-bold tracking-[-0.03em] text-forest-800">Libera</span>
                <span className="font-display text-[20px] font-bold tracking-[-0.03em] text-gold-400">Cure</span>
              </Link>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-2 text-warm-400 hover:text-forest-700 rounded-lg hover:bg-forest-50 cursor-pointer"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* User section */}
            <div className="p-5 border-b border-warm-200">
              {!isLoggedIn ? (
                <div className="flex gap-3">
                  <Link to="/login" onClick={() => setIsMobileMenuOpen(false)} className="flex-1 btn-primary text-center text-sm justify-center py-3">
                    Sign In
                  </Link>
                  <Link to="/signup" onClick={() => setIsMobileMenuOpen(false)} className="flex-1 btn-secondary text-center text-sm justify-center py-3">
                    Sign Up
                  </Link>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-forest-600 to-forest-800 flex items-center justify-center">
                    <span className="text-sm font-bold text-cream-100">{user?.name?.charAt(0)?.toUpperCase() || 'U'}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="block text-[15px] font-semibold text-forest-900 truncate">{user?.name}</span>
                    <Link to="/mypage" onClick={() => setIsMobileMenuOpen(false)} className="text-[12px] text-gold-500 font-medium">View profile →</Link>
                  </div>
                  <button onClick={() => { logout(); setIsMobileMenuOpen(false); }} className="p-2 text-warm-400 hover:text-forest-700 rounded-lg hover:bg-forest-50 cursor-pointer">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
                    </svg>
                  </button>
                </div>
              )}
            </div>

            {/* Navigation */}
            <div className="p-4">
              <div className="space-y-0.5">
                {mobileNavLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`flex items-center justify-between px-4 py-3.5 text-[15px] font-medium rounded-xl transition-all duration-200 ${
                      isActive(link.to)
                        ? 'text-forest-900 bg-forest-50'
                        : 'text-warm-600 hover:bg-forest-50/60 hover:text-forest-900'
                    }`}
                  >
                    {link.label}
                    <svg className="w-4 h-4 text-warm-300" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                    </svg>
                  </Link>
                ))}
              </div>
            </div>

            {/* Botanical divider */}
            <div className="px-8">
              <div className="divider-botanical" />
            </div>

            {/* Categories grid */}
            <div className="px-4 pb-4 pt-4">
              <p className="text-[11px] font-semibold text-warm-400 uppercase tracking-[0.15em] mb-3 px-4">Categories</p>
              <div className="grid grid-cols-2 gap-2">
                {hierarchicalCategories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/shop/${encodeURIComponent(cat.name)}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex flex-col items-center gap-2 p-4 bg-cream-100 rounded-xl hover:bg-forest-50 transition-all duration-200 text-center group"
                  >
                    <div className="w-9 h-9 rounded-lg bg-cream-50 group-hover:bg-forest-100 flex items-center justify-center text-warm-400 group-hover:text-forest-600 transition-colors shadow-subtle">
                      {categoryIcons[cat.name] || (
                        <svg className="w-4.5 h-4.5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
                        </svg>
                      )}
                    </div>
                    <span className="text-[13px] font-medium text-warm-600 group-hover:text-forest-700">{formatCategoryLabel(cat.name)}</span>
                  </Link>
                ))}
              </div>
            </div>

            {/* Bottom utilities */}
            <div className="p-4 border-t border-warm-200 mt-auto">
              <div className="space-y-0.5">
                <Link to="/inquiry" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 px-4 py-3 text-[14px] text-warm-500 rounded-xl hover:bg-forest-50 hover:text-forest-700 transition-colors">
                  <svg className="w-4.5 h-4.5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" />
                  </svg>
                  Help Center
                </Link>
                <Link to="/tracking" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 px-4 py-3 text-[14px] text-warm-500 rounded-xl hover:bg-forest-50 hover:text-forest-700 transition-colors">
                  <svg className="w-4.5 h-4.5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.079-.481 1.09-1.102a17.09 17.09 0 00-1.305-6.148l-1.57-3.14a3.375 3.375 0 00-3.015-1.86H12.75M9.75 6.75H3.375c-.621 0-1.125.504-1.125 1.125v8.25c0 .621.504 1.125 1.125 1.125h.375" />
                  </svg>
                  Track Order
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
